import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import sttService, {
  DEFAULT_SPEAKER_MAP,
  mapSpeakerLabel,
} from '../services/sttService';
import { patientGeneratingPath } from '../constants/routes';
import { useSession } from './SessionContext';
import { useLanguage } from './LanguageContext';

const AdmissionAmbientContext = createContext(null);
const AUDIO_BUFFER_SIZE = 2048;

export function AdmissionAmbientProvider({ children }) {
  const navigate = useNavigate();
  const { setIsRecordingActive } = useSession();
  const { t } = useLanguage();

  const [active, setActive] = useState(false);
  const [patientId, setPatientId] = useState(null);
  const [patientMeta, setPatientMeta] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [utterances, setUtterances] = useState([]);
  const [interim, setInterim] = useState(null);
  const [speakerMap, setSpeakerMap] = useState({ ...DEFAULT_SPEAKER_MAP });
  const [error, setError] = useState(null);

  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const sampleRateRef = useRef(16000);
  const isMountedRef = useRef(true);
  const sessionActiveRef = useRef(false);
  const finishingRef = useRef(false);

  const cleanupAudio = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  }, []);

  const teardownSession = useCallback(() => {
    sttService.stop();
    cleanupAudio();
    sessionActiveRef.current = false;
    if (isMountedRef.current) {
      setIsRecording(false);
      setIsConnecting(false);
      setIsRecordingActive(false);
    }
  }, [cleanupAudio, setIsRecordingActive]);

  const resetState = useCallback(() => {
    setActive(false);
    setPatientId(null);
    setPatientMeta(null);
    setUtterances([]);
    setInterim(null);
    setSpeakerMap({ ...DEFAULT_SPEAKER_MAP });
    setError(null);
    finishingRef.current = false;
  }, []);

  const cancelAdmission = useCallback(() => {
    teardownSession();
    resetState();
  }, [resetState, teardownSession]);

  const finishAdmission = useCallback(() => {
    if (finishingRef.current) return;
    finishingRef.current = true;

    const transcript = sttService.getLiveTranscript();
    const diarizedUtterances = sttService.getUtterances();
    const pid = patientId;

    teardownSession();
    resetState();

    if (!transcript.trim()) {
      if (isMountedRef.current) {
        setError(t('noSpeechDetected'));
      }
      finishingRef.current = false;
      return;
    }

    navigate(patientGeneratingPath(pid), {
      state: {
        transcript,
        diarizedUtterances,
        speakerMap,
        encounterDateTime: new Date().toISOString(),
        forcedNoteTypes: ['admission'],
        admissionMeta: patientMeta,
      },
    });
  }, [navigate, patientId, patientMeta, resetState, speakerMap, t, teardownSession]);

  const startAmbientSession = useCallback(async () => {
    if (sessionActiveRef.current) return;

    setError(null);
    setIsConnecting(true);
    setUtterances([]);
    setInterim(null);

    try {
      const [, stream] = await Promise.all([
        sttService.prefetchJWT(),
        navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
          },
        }),
      ]);

      if (!isMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;
      sessionActiveRef.current = true;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      sampleRateRef.current = audioContext.sampleRate;

      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;

      const processor = audioContext.createScriptProcessor(AUDIO_BUFFER_SIZE, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (event) => {
        if (!sessionActiveRef.current) return;
        const inputData = event.inputBuffer.getChannelData(0);
        sttService.sendAudio(new Float32Array(inputData).buffer);
      };

      const mute = audioContext.createGain();
      mute.gain.value = 0;
      mute.connect(audioContext.destination);

      await sttService.start({
        mode: 'admission',
        sampleRate: sampleRateRef.current,
        onConnected: () => {
          if (!isMountedRef.current) return;
          setIsConnecting(false);
          setIsRecording(true);
          setIsRecordingActive(true);
        },
        onDiarizedTranscript: ({ utterances: nextUtterances, interim: nextInterim }) => {
          if (!isMountedRef.current) return;
          setUtterances(nextUtterances);
          setInterim(nextInterim);
        },
        onError: (err) => {
          if (!isMountedRef.current) return;
          const detail = String(err?.message || '').trim();
          const message =
            err?.message?.includes('connect') || err?.message?.includes('internet')
              ? t('connectionError')
              : (detail && !/transcription error/i.test(detail) ? detail : t('transcriptionError'));
          setError(message);
          teardownSession();
        },
        onDisconnected: () => {
          if (!isMountedRef.current || finishingRef.current) return;
          teardownSession();
        },
      });

      if (!isMountedRef.current) return;

      source.connect(processor);
      processor.connect(mute);
    } catch (err) {
      if (!isMountedRef.current) return;
      setError(err?.name === 'NotAllowedError' ? t('micDenied') : t('connectionError'));
      setIsConnecting(false);
      cleanupAudio();
      sessionActiveRef.current = false;
    }
  }, [cleanupAudio, setIsRecordingActive, t, teardownSession]);

  const beginAdmission = useCallback(
    (id, meta) => {
      setPatientId(id);
      setPatientMeta(meta);
      setActive(true);
      setSpeakerMap({ ...DEFAULT_SPEAKER_MAP });
    },
    []
  );

  const swapSpeakerLabels = useCallback(() => {
    setSpeakerMap((prev) => ({
      S1: prev.S2 === 'Patient' ? 'Nurse' : 'Patient',
      S2: prev.S1 === 'Nurse' ? 'Patient' : 'Nurse',
    }));
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      teardownSession();
    };
  }, [teardownSession]);

  const value = {
    active,
    patientId,
    patientMeta,
    isConnecting,
    isRecording,
    utterances,
    interim,
    speakerMap,
    error,
    beginAdmission,
    startAmbientSession,
    finishAdmission,
    cancelAdmission,
    swapSpeakerLabels,
    mapSpeaker: (speaker) => mapSpeakerLabel(speaker, speakerMap),
  };

  return (
    <AdmissionAmbientContext.Provider value={value}>
      {children}
    </AdmissionAmbientContext.Provider>
  );
}

export function useAdmissionAmbient() {
  const ctx = useContext(AdmissionAmbientContext);
  if (!ctx) {
    throw new Error('useAdmissionAmbient must be used within AdmissionAmbientProvider');
  }
  return ctx;
}
